import { Component, OnDestroy, OnInit } from '@angular/core';
import { finalize, Subject, takeUntil } from 'rxjs';
import { BreadcrumbService } from 'src/app/service/breadcrumb.service';
import { UserService } from 'src/app/service/user.service';
import { ConfirmationService, MessageService } from 'primeng/api';
import { DataTablePage } from 'src/app/models/data-table-page.model';
import { Contract } from 'src/app/models/user.model';
import {
  AcaoLog,
  EquipamentoInicioOperacao,
  EquipamentoInicioOperacaoFilter,
  EquipamentoInicioOperacaoLog,
  EquipamentoInicioOperacaoLogFilter,
  EquipamentoLegacy,
  LoteInicioOperacao,
} from 'src/app/models/equipamento-inicio-operacao.model';
import { EquipmentOperationStartService } from 'src/app/service/equipment-operation-start.service';
import { UserTypeEnum } from 'src/app/shared/enums/user-type.enum';

interface LogDiffRow {
  campo: string;
  anterior: string;
  atualizado: string;
  muted: boolean;
  showAnterior: boolean;
  showAtualizado: boolean;
}

@Component({
  selector: 'app-equipment-operation-start',
  templateUrl: './equipment-operation-start.component.html',
  styleUrls: ['./equipment-operation-start.component.scss'],
})
export class EquipmentOperationStartComponent implements OnInit, OnDestroy {

  private _destroy$ = new Subject<void>();

  stylePattern = { 'min-width': '100%', 'max-width': '100%', 'height': '40px' };

  page!: DataTablePage;
  loading = false;
  buttonLoading = false;
  first = 0;
  totalRecords = 0;

  contracts: Contract[] = [];
  equipamentos: EquipamentoLegacy[] = [];
  lotes: LoteInicioOperacao[] = [];
  empresasOptions: { label: string; value: string }[] = [];
  data: EquipamentoInicioOperacao[] = [];
  existingEquipamentoIds = new Set<number>();

  notFound = false;
  tituloNotFound = 'Nenhum registro encontrado';
  descricaoNotFound = 'Desculpe, não encontramos nenhum registro para os filtros informados';

  showDialog = false;
  editMode = false;
  currentId: number | null = null;

  filter: EquipamentoInicioOperacaoFilter = {};
  currentFilter: EquipamentoInicioOperacaoFilter = {};

  form: {
    equipamentoId: number | null;
    loteId: number | null;
    dataInicioOperacao: Date | null;
    dataFimOperacao: Date | null;
    dataInicioFaturamento: Date | null;
    ativo: boolean;
    empresa: string | null;
  } = this.emptyForm();

  statusOptions = [
    { label: 'Todos', value: null },
    { label: 'Ativo', value: true },
    { label: 'Inativo', value: false },
  ];

  columns = [
    { field: 'equipamentoNome', label: 'Equipamento', width: '14%' },
    { field: 'empresa', label: 'Empresa', width: '12%' },
    { field: 'dataInicioOperacao', label: 'Início Operação', width: '13%' },
    { field: 'dataFimOperacao', label: 'Fim Operação', width: '13%' },
    { field: 'dataInicioFaturamento', label: 'Início Faturamento', width: '13%' },
    { field: 'loteId', label: 'Lote', width: '14%' },
    { field: 'ativo', label: 'Status', width: '9%' },
  ];

  activeTabIndex = 0;

  historyPage!: DataTablePage;
  historyFirst = 0;
  historyTotalRecords = 0;
  historyLoading = false;
  historyData: EquipamentoInicioOperacaoLog[] = [];
  historyNotFound = false;
  historyLoaded = false;
  historyFiltersLoaded = false;

  historyFilter: EquipamentoInicioOperacaoLogFilter = {};
  historyCurrentFilter: EquipamentoInicioOperacaoLogFilter = {};

  historyDetail: EquipamentoInicioOperacaoLog | null = null;
  historyDetailVisible = false;
  historyDetailRows: LogDiffRow[] = [];
  historyDetailChangedCount = 0;

  acaoOptions: { label: string; value: AcaoLog }[] = [];
  usuariosOptions: { label: string; value: number }[] = [];

  private readonly logFieldLabels: { [key: string]: string | null } = {
    equipamentoId: 'Equipamento',
    dataInicioOperacao: 'Início Operação',
    dataFimOperacao: 'Fim Operação',
    dataInicioFaturamento: 'Início Faturamento',
    loteId: 'Lote',
    empresa: 'Empresa',
    ativo: 'Status',
    id: null,
    contratoId: null,
    userId: null,
    deleted: null,
    createdAt: null,
    updatedAt: null,
  };

  constructor(
    private breadCrumb: BreadcrumbService,
    private userService: UserService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private service: EquipmentOperationStartService,
  ) {
    this.page = new DataTablePage();
    this.page.size = 10;
    this.page.pageNumber = 0;

    this.historyPage = new DataTablePage();
    this.historyPage.size = 10;
    this.historyPage.pageNumber = 0;
  }

  get isAdmin(): boolean {
    return this.userService.user?.userType === UserTypeEnum.ADMIN;
  }

  get formEquipamentos(): EquipamentoLegacy[] {
    if (this.editMode) return this.equipamentos;
    return this.equipamentos.filter((e) => !this.existingEquipamentoIds.has(e.equipamentoId));
  }

  ngOnInit(): void {
    this.breadCrumb.next([
      { label: 'Monitoramento', icon: 'pi pi-desktop' },
      { label: 'Início de Operação', icon: 'pi pi-play' },
    ]);
    this.initContract();
  }

  private initContract(): void {
    const contracts = this.userService.getCurrentContractAsList();
    if (!contracts?.[0]?.id) return;

    this.contracts = contracts as Contract[];
    this.filter.contratoId = contracts[0].id;
    this.loadFilters();
    this.loadLotes();
    this.loadEquipamentos();
    this.loadExistingEquipamentoIds();
  }

  private loadFilters(): void {
    this.service.getFilters()
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (res) => {
          this.empresasOptions = (res?.empresas ?? []).map((empresa: string) => ({
            label: empresa,
            value: empresa,
          }));
        },
        error: () => this.empresasOptions = [],
      });
  }

  private loadExistingEquipamentoIds(): void {
    const page = new DataTablePage();
    page.size = 1000;
    page.pageNumber = 0;

    this.service.getList(page, { contratoId: this.filter.contratoId })
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (res) => {
          this.existingEquipamentoIds = new Set(
            (res.content ?? []).map((r: EquipamentoInicioOperacao) => r.equipamentoId)
          );
        },
        error: () => this.existingEquipamentoIds = new Set(),
      });
  }

  private loadEquipamentos(): void {
    this.service.getEquipamentos(this.filter.contratoId!)
      .pipe(takeUntil(this._destroy$))
      .subscribe((response: any) => {
        const uniqueEquipamentos = new Map<string, EquipamentoLegacy>();
        response.forEach((item: any) => {
          if (!uniqueEquipamentos.has(item.nomeExterno)) {
            uniqueEquipamentos.set(item.nomeExterno, item);
          }
        });
        this.equipamentos = Array.from(uniqueEquipamentos.values());
      });
  }

  private loadLotes(): void {
    this.service.getLotes()
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (r) => this.lotes = r,
        error: () => this.lotes = [],
      });
  }

  onLazyLoad(event: any): void {
    this.page.size = event.rows;
    this.page.pageNumber = event.first / event.rows;
    this.loadData();
  }

  applyFilter(): void {
    this.first = 0;
    this.page.pageNumber = 0;
    this.currentFilter = { ...this.filter };
    this.loadData();
  }

  clearFilter(): void {
    const contratoId = this.filter.contratoId;
    this.filter = { contratoId };
    this.currentFilter = { contratoId };
    this.first = 0;
    this.page.pageNumber = 0;
    this.loadData();
  }

  onFiltroDataChange(): void {
    const inicio = this.filter.dataInicioOperacao ?? null;
    const fim = this.filter.dataFimOperacao ?? null;
    const faturamento = this.filter.dataInicioFaturamento ?? null;

    if (inicio && fim && fim.getTime() < inicio.getTime()) {
      this.filter.dataFimOperacao = null;
    }
    if (faturamento) {
      if (inicio && faturamento.getTime() < inicio.getTime()) {
        this.filter.dataInicioFaturamento = null;
      } else if (this.filter.dataFimOperacao && faturamento.getTime() > this.filter.dataFimOperacao.getTime()) {
        this.filter.dataInicioFaturamento = null;
      }
    }
  }

  onFormDataChange(): void {
    const inicio = this.form.dataInicioOperacao ?? null;
    const fim = this.form.dataFimOperacao ?? null;
    const faturamento = this.form.dataInicioFaturamento ?? null;

    if (inicio && fim && fim.getTime() < inicio.getTime()) {
      this.form.dataFimOperacao = null;
    }
    if (faturamento) {
      if (inicio && faturamento.getTime() < inicio.getTime()) {
        this.form.dataInicioFaturamento = null;
      } else if (this.form.dataFimOperacao && faturamento.getTime() > this.form.dataFimOperacao.getTime()) {
        this.form.dataInicioFaturamento = null;
      }
    }
  }

  private loadData(): void {
    this.notFound = false;
    this.loading = true;
    this.loadHistory();

    this.service.getList(this.page, this.currentFilter)
      .pipe(
        takeUntil(this._destroy$),
        finalize(() => this.loading = false)
      )
      .subscribe({
        next: (res) => {
          this.totalRecords = this.page.totalElements;
          this.data = res.content ?? [];
          if (!this.data.length) {
            this.notFound = true;
          }
        },
        error: () => {
          this.data = [];
          this.notFound = true;
        },
      });
  }

  openCreate(): void {
    this.editMode = false;
    this.currentId = null;
    this.form = this.emptyForm();
    this.showDialog = true;
  }

  openEdit(record: EquipamentoInicioOperacao): void {
    this.editMode = true;
    this.currentId = record.id!;
    this.form = {
      equipamentoId: record.equipamentoId,
      loteId: record.loteId ?? null,
      dataInicioOperacao: record.dataInicioOperacao ? new Date(record.dataInicioOperacao) : null,
      dataFimOperacao: record.dataFimOperacao ? new Date(record.dataFimOperacao) : null,
      dataInicioFaturamento: record.dataInicioFaturamento ? new Date(record.dataInicioFaturamento) : null,
      ativo: record.ativo ?? true,
      empresa: record.empresa ?? null,
    };
    this.showDialog = true;
  }

  save(): void {
    if (!this.isFormValid()) return;

    this.buttonLoading = true;
    const payload = this.buildPayload();

    const request$ = this.editMode
      ? this.service.update(this.currentId!, payload)
      : this.service.create(payload);

    request$
      .pipe(
        takeUntil(this._destroy$),
        finalize(() => this.buttonLoading = false)
      )
      .subscribe({
        next: () => {
          this.showDialog = false;
          this.messageService.add({
            key: 'toastSuccess',
            severity: 'success',
            detail: this.editMode
              ? 'Início de operação atualizado com sucesso!'
              : 'Início de operação criado com sucesso!',
          });
          this.loadData();
          this.loadExistingEquipamentoIds();
        },
        error: () => {
          this.messageService.add({
            key: 'toastError',
            severity: 'error',
            summary: 'Erro',
            detail: this.editMode
              ? 'Erro ao atualizar o início de operação.'
              : 'Erro ao criar o início de operação.',
          });
        },
      });
  }

  openDeleteConfirm(record: EquipamentoInicioOperacao): void {
    this.confirmationService.confirm({
      message: `Você realmente deseja excluir o início de operação do equipamento: ${record.equipamentoNome ?? record.equipamentoId}?`,
      header: 'Excluir Início de Operação',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Excluir',
      rejectLabel: 'Cancelar',
      acceptIcon: 'pi pi-trash mr-1',
      rejectIcon: 'pi pi-times mr-1',
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-secondary mr-2',
      accept: () => this.deleteRecord(record.id!),
      reject: () => {},
    });
  }

  private deleteRecord(id: number): void {
    this.service.delete(id, this.userService.user.id)
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: () => {
          this.loadData();
          this.loadExistingEquipamentoIds();
          this.messageService.add({
            key: 'toastSuccess',
            severity: 'success',
            detail: 'Início de operação excluído com sucesso!',
          });
        },
        error: () => {
          this.messageService.add({
            key: 'toastError',
            severity: 'error',
            summary: 'Erro',
            detail: 'Erro ao excluir o início de operação.',
          });
        },
      });
  }

  getLoteNome(loteId: number | null | undefined): string {
    if (loteId == null) return '-';
    return this.lotes.find((l) => l.id === loteId)?.nome ?? String(loteId);
  }

  getEquipamentoNome(equipamentoId: number | null | undefined): string {
    if (equipamentoId == null) return '-';
    return (
      this.equipamentos.find((e) => e.equipamentoId === equipamentoId)?.nomeExterno ??
      String(equipamentoId)
    );
  }

  isFormValid(): boolean {
    if (!this.form.equipamentoId) {
      this.showError('Campo Equipamento é obrigatório.');
      return false;
    }
    if (!this.form.empresa) {
      this.showError('Campo Empresa é obrigatório.');
      return false;
    }
    if (!this.editMode && this.existingEquipamentoIds.has(this.form.equipamentoId)) {
      this.showError('Este equipamento já possui um início de operação cadastrado.');
      return false;
    }
    if (!this.form.dataInicioOperacao) {
      this.showError('Campo Data Início Operação é obrigatório.');
      return false;
    }
    if (!this.form.dataFimOperacao) {
      this.showError('Campo Data Fim Operação é obrigatório.');
      return false;
    }
    if (!this.form.loteId) {
      this.showError('Campo Lote é obrigatório.');
      return false;
    }
    return true;
  }

  private buildPayload() {
    return {
      contratoId: this.getContratoOriginId()!,
      equipamentoId: this.form.equipamentoId!,
      dataInicioOperacao: this.formatDate(this.form.dataInicioOperacao!),
      dataFimOperacao: this.formatDate(this.form.dataFimOperacao!),
      dataInicioFaturamento: this.form.dataInicioFaturamento
        ? this.formatDate(this.form.dataInicioFaturamento)
        : null,
      loteId: this.form.loteId ?? null,
      ativo: this.form.ativo,
      empresa: this.form.empresa!,
      userId: this.userService.user.id,
    };
  }

  private getContratoOriginId(): number | undefined {
    return this.contracts.find((c) => c.id === this.filter.contratoId)?.contractOriginId
      ?? this.filter.contratoId ?? undefined;
  }

  private formatDate(date: Date): string {
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} 00:00:00`;
  }

  private showError(message: string): void {
    this.messageService.add({ key: 'toastError', severity: 'error', detail: message });
  }

  private emptyForm() {
    return {
      equipamentoId: null as number | null,
      loteId: null as number | null,
      dataInicioOperacao: null as Date | null,
      dataFimOperacao: null as Date | null,
      dataInicioFaturamento: null as Date | null,
      ativo: true,
      empresa: null as string | null,
    };
  }

  onTabChange(event: any): void {
    if (event?.index !== 1) return;

    if (!this.historyFiltersLoaded) {
      this.historyFiltersLoaded = true;
      this.loadHistoryFilters();
    }
    if (!this.historyLoaded) {
      this.historyCurrentFilter = { ...this.historyFilter };
      this.loadHistory();
    }
  }

  private loadHistoryFilters(): void {
    this.service.getHistoryFilters()
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (res) => {
          this.usuariosOptions = (res?.usuarios ?? []).map((u: any) => ({ label: u.nome, value: u.id }));
          this.acaoOptions = (res?.acoes ?? []).map((a: AcaoLog) => ({
            label: this.getTipoOperacaoLabel(a),
            value: a,
          }));
        },
        error: () => {
          this.usuariosOptions = [];
          this.acaoOptions = [];
        },
      });
  }

  onHistoryLazyLoad(event: any): void {
    this.historyPage.size = event.rows;
    this.historyPage.pageNumber = event.first / event.rows;
    this.loadHistory();
  }

  applyHistoryFilter(): void {
    this.historyFirst = 0;
    this.historyPage.pageNumber = 0;
    this.historyCurrentFilter = { ...this.historyFilter };
    this.loadHistory();
  }

  clearHistoryFilter(): void {
    this.historyFilter = {};
    this.historyCurrentFilter = {};
    this.historyFirst = 0;
    this.historyPage.pageNumber = 0;
    this.loadHistory();
  }

  private loadHistory(): void {
    this.historyNotFound = false;
    this.historyLoading = true;

    this.service.getHistory(this.historyPage, this.historyCurrentFilter)
      .pipe(
        takeUntil(this._destroy$),
        finalize(() => {
          this.historyLoading = false;
          this.historyLoaded = true;
        })
      )
      .subscribe({
        next: (res) => {
          this.historyTotalRecords = this.historyPage.totalElements;
          this.historyData = res.content ?? [];
          if (!this.historyData.length) {
            this.historyNotFound = true;
          }
        },
        error: () => {
          this.historyData = [];
          this.historyNotFound = true;
          this.messageService.add({
            key: 'toastError',
            severity: 'error',
            summary: 'Erro',
            detail: 'Erro ao carregar o histórico.',
          });
        },
      });
  }

  openHistoryDetail(record: EquipamentoInicioOperacaoLog): void {
    this.historyDetail = record;
    this.historyDetailRows = this.getDiffRows(record);
    this.historyDetailChangedCount = this.historyDetailRows
      .filter((r) => r.showAnterior || r.showAtualizado).length;
    this.historyDetailVisible = true;
  }

  getOperacaoModalLabel(acao: AcaoLog): string {
    switch (acao) {
      case 'CREATE': return 'Adicionado';
      case 'UPDATE': return 'Editado';
      case 'DELETE': return 'Excluído';
      default: return String(acao ?? '-');
    }
  }

  getTipoOperacaoLabel(acao: AcaoLog): string {
    switch (acao) {
      case 'CREATE': return 'Inclusão';
      case 'UPDATE': return 'Alteração';
      case 'DELETE': return 'Exclusão';
      default: return String(acao ?? '-');
    }
  }

  getTipoOperacaoSeverity(acao: AcaoLog): string {
    switch (acao) {
      case 'CREATE': return 'success';
      case 'UPDATE': return 'warning';
      case 'DELETE': return 'danger';
      default: return 'info';
    }
  }

  getTagSoftClass(severity: string): string {
    switch (severity) {
      case 'success': return 'bg-green-100 text-green-700';
      case 'danger': return 'bg-red-100 text-red-700';
      case 'warning': return 'bg-orange-100 text-orange-700';
      case 'info': return 'bg-blue-100 text-blue-700';
      default: return 'bg-blue-100 text-blue-700';
    }
  }

  getDiffRows(record: EquipamentoInicioOperacaoLog): LogDiffRow[] {
    const acao = record?.acao;
    const anterior = this.parseRegistro(record?.registroAntigo);
    const atualizado = this.parseRegistro(record?.registroAtual);
    const present = new Set([...Object.keys(anterior), ...Object.keys(atualizado)]);
    const known = Object.keys(this.logFieldLabels);
    const keys = [
      ...known.filter((k) => present.has(k)),
      ...Array.from(present).filter((k) => !known.includes(k)),
    ].filter((k) => this.logFieldLabels[k] !== null);

    return keys.map((key) => {
      const ant = this.formatLogValue(key, anterior[key]);
      const atu = this.formatLogValue(key, atualizado[key]);
      const changed = ant !== atu;

      return {
        campo: this.logFieldLabels[key] ?? key,
        anterior: ant,
        atualizado: atu,
        muted: acao === 'UPDATE' && !changed,
        showAnterior: acao === 'DELETE' || (acao === 'UPDATE' && changed),
        showAtualizado: acao === 'CREATE' || (acao === 'UPDATE' && changed),
      };
    });
  }

  private parseRegistro(value: any): { [key: string]: any } {
    if (value == null) return {};
    if (typeof value === 'string') {
      try {
        return JSON.parse(value) ?? {};
      } catch {
        return {};
      }
    }
    return value;
  }

  private formatLogValue(key: string, value: any): string {
    if (value == null) return '-';
    if (key === 'ativo') return value ? 'Ativo' : 'Inativo';
    if (key === 'equipamentoId') return this.getEquipamentoNome(value);
    if (key === 'loteId') return this.getLoteNome(value);
    if (typeof value === 'boolean') return value ? 'Sim' : 'Não';
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
      const d = new Date(value.replace(' ', 'T'));
      if (!isNaN(d.getTime())) {
        const pad = (n: number) => n.toString().padStart(2, '0');
        return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
      }
    }
    return String(value);
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }
}
